import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MapPin, Navigation, CheckCircle2, AlertTriangle, Users, LocateFixed } from 'lucide-react';
import { toast } from 'sonner';
import { useMosque } from '../contexts/MosqueContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PRAYERS = ['Subuh', 'Dzuhur', 'Ashar', 'Maghrib', 'Isya'];

const AbsenPage = () => {
  const { mosque } = useMosque();
  const [prayer, setPrayer] = useState('Subuh');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [today, setToday] = useState({ count: 0, recent: [] });

  const hasCoords = mosque?.latitude != null && mosque?.longitude != null;

  const loadToday = () => axios.get(`${API_URL}/api/attendance/today`, { params: { mosque_id: mosque?.id } }).then((r) => setToday(r.data)).catch(() => {});
  useEffect(() => { if (mosque?.id) loadToday(); /* eslint-disable-next-line */ }, [mosque?.id]);

  const doAbsen = () => {
    if (!navigator.geolocation) return toast.error('Perangkat tidak mendukung GPS');
    setBusy(true); setResult(null);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await axios.post(`${API_URL}/api/attendance`, {
          mosque_id: mosque.id, prayer, name,
          latitude: pos.coords.latitude, longitude: pos.coords.longitude,
        });
        setResult({ ok: true, ...data }); loadToday();
      } catch (e) {
        setResult({ ok: false, detail: e.response?.data?.detail || 'Gagal absen' });
      } finally { setBusy(false); }
    }, () => { setBusy(false); toast.error('Izin lokasi ditolak. Aktifkan GPS untuk absen.'); }, { enableHighAccuracy: true, timeout: 10000 });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-[#F8F9FA] px-6 py-8 pb-24" data-testid="absen-page">
      <div className="mb-6">
        <h1 className="text-4xl font-heading font-bold tracking-tight text-primary-dark mb-2">Absen Berjamaah</h1>
        <p className="text-sm font-body text-text-secondary">Absen aktif hanya dalam radius 50 meter dari masjid</p>
      </div>

      {!hasCoords && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex items-start gap-3" data-testid="absen-no-coords">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-body text-amber-800">Koordinat masjid belum diatur oleh takmir. Absen belum bisa digunakan.</p>
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6 mb-6">
        <label className="block text-xs font-body font-semibold text-stone-500 uppercase mb-2">Waktu Sholat</label>
        <div className="grid grid-cols-5 gap-2 mb-4">
          {PRAYERS.map((p) => (
            <button key={p} data-testid={`absen-prayer-${p.toLowerCase()}`} onClick={() => setPrayer(p)}
              className={`py-2 rounded-xl text-xs font-body font-medium ${prayer === p ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-600'}`}>{p}</button>
          ))}
        </div>
        <input data-testid="absen-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama Anda (opsional)"
          className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none font-body text-sm mb-4" />
        <button data-testid="absen-submit-btn" onClick={doAbsen} disabled={busy || !hasCoords}
          className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-stone-300 text-white font-body font-semibold py-3.5 rounded-full">
          {busy ? <><LocateFixed className="w-5 h-5 animate-pulse" /> Mendeteksi lokasi...</> : <><Navigation className="w-5 h-5" /> Absen Sekarang</>}
        </button>

        {result && (
          <div className={`mt-4 rounded-2xl p-4 flex items-start gap-3 ${result.ok ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`} data-testid="absen-result">
            {result.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
            <div>
              <p className={`text-sm font-body font-semibold ${result.ok ? 'text-emerald-800' : 'text-red-800'}`}>{result.ok ? `Absen ${result.prayer} berhasil!` : 'Absen gagal'}</p>
              <p className={`text-xs font-body ${result.ok ? 'text-emerald-700' : 'text-red-700'}`}>{result.ok ? `Jarak Anda ${result.distance_m} m dari masjid.` : result.detail}</p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-emerald-700" />
          <h2 className="font-heading font-semibold text-primary-dark">Jamaah Hadir Hari Ini</h2>
          <span className="ml-auto text-2xl font-heading font-bold text-emerald-700" data-testid="absen-today-count">{today.count}</span>
        </div>
        {today.recent.length === 0 ? (
          <p className="text-sm font-body text-text-secondary text-center py-4">Belum ada yang absen hari ini</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {today.recent.map((r, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-body font-medium">
                <MapPin className="w-3 h-3" /> {r.name} · {r.prayer}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AbsenPage;
